import { Hub, type HubCapsule } from "@aws-amplify/core";
import Layout from "components/Layout/Layout";
import NoHeadLayout from "components/NoHeadLayout/NoHeadLayout";
import { FullScreenLoading } from "components/ui/Loading/Loading";
import { AuthContext } from "hooks/authProvider/authProvider";
import {
  useAnalyticsPageView,
  useLogicalPageView,
} from "hooks/use-analytics-page-view";
import { usePublicFeatureFlags } from "hooks/usePublicFeatureFlags";
import type React from "react";
import { type FC, useCallback, useContext, useEffect } from "react";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useNavigate,
  useParams,
} from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import AboutPage from "routes/AboutPage";
import { CompaniesPage, CompanyDetailPage } from "routes/CompaniesPage";
import {
  CompanyGroupDetailPage,
  CompanyGroupsPage,
} from "routes/CompanyGroupsPage";
import ContractPage from "routes/crm/ContractPage";
import CrmAccessDeniedPage from "routes/crm/CrmAccessDeniedPage";
import CrmIndexPage from "routes/crm/CrmIndexPage";
import CrmNotificationSettingsPage from "routes/crm/CrmNotificationSettingsPage";
import SalesBoardPage from "routes/crm/SalesBoardPage";
import TalentDetailPage from "routes/crm/TalentDetailPage";
import TalentFormPage from "routes/crm/TalentFormPage";
import TalentListPage from "routes/crm/TalentListPage";
import {
  DeliveriesPage,
  DeliveryDetailPage,
  DeliveryEditPage,
  DeliveryNewPage,
} from "routes/DeliveryPage";
import {
  DeliverySeriesDetailPage,
  DeliverySeriesListPage,
} from "routes/DeliverySeriesPage";
import { EmailDetailPageContainer } from "routes/emails/EmailDetailPage";
import EmailRepliesPage from "routes/emails/EmailRepliesPage";
import SearchEmailsPage from "routes/emails/SearchEmailsPage";
import TemplateSamplePage from "routes/emails/TemplateSamplePage";
import AdminRoute from "routes/guards/AdminRoute";
import CrmRoute from "routes/guards/CrmRoute";
import TenantAdminRoute from "routes/guards/TenantAdminRoute";
import HomePage from "routes/HomePage";
import InvitationPage from "routes/InvitationPage";
import LandingPage from "routes/LandingPage";
import { LpDeliveryRoute } from "routes/LandingPageDelivery/LpDeliveryRoute";
import MatterPage from "routes/MatterPage/MatterPage";
import MatchingPage from "routes/matching/MatchingPage";
import MatchingResultsPage from "routes/matching/MatchingResultsPage";
import OnboardingPage from "routes/OnboardingPage";
import ProfilePage from "routes/ProfilePage";
import EmailOAuthCallbackPage from "routes/profile/EmailOAuthCallbackPage";
import RedirectPage from "routes/RedirectPage";
import ResetPassWordPage from "routes/ResetPassWordPage";
import SignInPage from "routes/SignInPage";
import SignUpPage from "routes/SignUpPage";
import SnippetsPage from "routes/SnippetsPage/SnippetsPage";
import SamplesPage from "routes/samples/SamplesPage";
import InvitationsSettingsPage from "routes/settings/InvitationsPage";
import MatchingConditionFormPage from "routes/settings/MatchingConditionFormPage";
import MatchingConditionsPage from "routes/settings/MatchingConditionsPage";
import PresetDetailPage from "routes/settings/PresetDetailPage";
import PresetFormPage from "routes/settings/PresetFormPage";
import PresetsPage from "routes/settings/PresetsPage";
import SenderEmailsPage from "routes/settings/SenderEmailsPage";
import SlackOAuthCallbackPage from "routes/settings/SlackOAuthCallbackPage";
import TenantSettingsPage from "routes/settings/TenantPage";
import UsagePage from "routes/usage/UsagePage";

/**
 * @return {Router} ルーター
 */
function App() {
  return (
    <Router>
      <AnalyticsPageViewTracker />
      <Routes>
        <Route
          path="/signup"
          element={
            <NoHeadLayout>
              <SignUpPage />
            </NoHeadLayout>
          }
        />
        <Route
          path="/login"
          element={
            <NoHeadLayout>
              <SignInPage />
            </NoHeadLayout>
          }
        />
        <Route
          path="/reset-pass-word"
          element={
            <NoHeadLayout>
              <ResetPassWordPage />
            </NoHeadLayout>
          }
        />
        <Route
          path="/invitations/:token"
          element={
            <NoHeadLayout>
              <InvitationPage />
            </NoHeadLayout>
          }
        />
        <Route path="/lp" element={<LpRoute />} />
        <Route path="/lp/delivery" element={<LpDeliveryRoute />} />
        <Route
          path="/settings/slack/callback"
          element={<SlackOAuthCallbackPage />}
        />
        <Route
          path="/profile/email-integrations/:provider/callback"
          element={<EmailOAuthCallbackPage />}
        />
        <Route path="/" element={<RootRoute />} />
        <Route
          path="/*"
          element={
            <AuthRedirect>
              <Layout>
                <Routes>
                  <Route path="/redirect" element={<RedirectPage />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route
                    path="/profile"
                    element={
                      <PrivateRoute>
                        <ProfilePage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/matter"
                    element={
                      <PrivateRoute>
                        <MatterPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/emails/search/:id"
                    element={
                      <PrivateRoute>
                        <EmailDetailPageContainer />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/emails/search"
                    element={
                      <PrivateRoute>
                        <SearchEmailsPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/emails/replies"
                    element={
                      <PrivateRoute>
                        <CrmRoute>
                          <EmailRepliesPage />
                        </CrmRoute>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/emails/template-sample"
                    element={
                      <PrivateRoute>
                        <TemplateSamplePage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/matching/results"
                    element={
                      <PrivateRoute>
                        <MatchingResultsPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/matching/free"
                    element={
                      <PrivateRoute>
                        <MatchingPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/samples"
                    element={
                      <PrivateRoute>
                        <SamplesPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/snippets"
                    element={
                      <PrivateRoute>
                        <SnippetsPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/deliveries"
                    element={
                      <PrivateRoute>
                        <DeliveriesPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/deliveries/new"
                    element={
                      <PrivateRoute>
                        <DeliveryNewPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/deliveries/:id"
                    element={
                      <PrivateRoute>
                        <DeliveryDetailPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/deliveries/:id/edit"
                    element={
                      <PrivateRoute>
                        <DeliveryEditPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/delivery-series"
                    element={
                      <PrivateRoute>
                        <DeliverySeriesListPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/delivery-series/:id"
                    element={
                      <PrivateRoute>
                        <DeliverySeriesDetailPage />
                      </PrivateRoute>
                    }
                  />
                  {/* ISSUE-803: 登録企業 / 企業グループ (設定配下のマスタデータ) */}
                  <Route
                    path="/settings/companies"
                    element={
                      <PrivateRoute>
                        <CompaniesPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/settings/companies/:id"
                    element={
                      <PrivateRoute>
                        <CompanyDetailPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/settings/company-groups"
                    element={
                      <PrivateRoute>
                        <CompanyGroupsPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/settings/company-groups/:id"
                    element={
                      <PrivateRoute>
                        <CompanyGroupDetailPage />
                      </PrivateRoute>
                    }
                  />
                  {/* 旧トップレベルパスからの後方互換リダイレクト */}
                  <Route
                    path="/companies"
                    element={<Navigate to="/settings/companies" replace />}
                  />
                  <Route
                    path="/companies/:id"
                    element={<RedirectCompanyDetail />}
                  />
                  <Route
                    path="/company-groups"
                    element={<Navigate to="/settings/company-groups" replace />}
                  />
                  <Route
                    path="/company-groups/:id"
                    element={<RedirectCompanyGroupDetail />}
                  />
                  <Route
                    path="/settings/tenant"
                    element={
                      <PrivateRoute>
                        <TenantAdminRoute>
                          <TenantSettingsPage />
                        </TenantAdminRoute>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/settings/invitations"
                    element={
                      <PrivateRoute>
                        <TenantAdminRoute>
                          <InvitationsSettingsPage />
                        </TenantAdminRoute>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/settings/matching/conditions"
                    element={
                      <PrivateRoute>
                        <TenantAdminRoute>
                          <MatchingConditionsPage />
                        </TenantAdminRoute>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/settings/matching/conditions/new"
                    element={
                      <PrivateRoute>
                        <TenantAdminRoute>
                          <MatchingConditionFormPage />
                        </TenantAdminRoute>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/settings/matching/conditions/:id/edit"
                    element={
                      <PrivateRoute>
                        <TenantAdminRoute>
                          <MatchingConditionFormPage />
                        </TenantAdminRoute>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/settings/sender-emails"
                    element={
                      <PrivateRoute>
                        <TenantAdminRoute>
                          <SenderEmailsPage />
                        </TenantAdminRoute>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/settings/presets"
                    element={
                      <PrivateRoute>
                        <TenantAdminRoute>
                          <PresetsPage />
                        </TenantAdminRoute>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/settings/presets/new"
                    element={
                      <PrivateRoute>
                        <TenantAdminRoute>
                          <PresetFormPage />
                        </TenantAdminRoute>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/settings/presets/:id/edit"
                    element={
                      <PrivateRoute>
                        <TenantAdminRoute>
                          <PresetFormPage />
                        </TenantAdminRoute>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/settings/presets/:id"
                    element={
                      <PrivateRoute>
                        <TenantAdminRoute>
                          <PresetDetailPage />
                        </TenantAdminRoute>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/usage"
                    element={
                      <PrivateRoute>
                        <UsagePage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/crm/access-denied"
                    element={
                      <PrivateRoute>
                        <CrmAccessDeniedPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/crm"
                    element={
                      <PrivateRoute>
                        <CrmRoute>
                          <CrmIndexPage />
                        </CrmRoute>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/crm/contracts"
                    element={
                      <PrivateRoute>
                        <CrmRoute>
                          <ContractPage />
                        </CrmRoute>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/crm/talents"
                    element={
                      <PrivateRoute>
                        <CrmRoute>
                          <TalentListPage />
                        </CrmRoute>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/crm/talents/new"
                    element={
                      <PrivateRoute>
                        <CrmRoute requiredRole="editor">
                          <TalentFormPage />
                        </CrmRoute>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/crm/talents/:id"
                    element={
                      <PrivateRoute>
                        <CrmRoute>
                          <TalentDetailPage />
                        </CrmRoute>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/crm/talents/:id/edit"
                    element={
                      <PrivateRoute>
                        <CrmRoute requiredRole="editor">
                          <TalentFormPage />
                        </CrmRoute>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/crm/sales-board"
                    element={
                      <PrivateRoute>
                        <CrmRoute>
                          <SalesBoardPage />
                        </CrmRoute>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/crm/notification-settings"
                    element={
                      <PrivateRoute>
                        <CrmRoute requiredRole="admin">
                          <CrmNotificationSettingsPage />
                        </CrmRoute>
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/admin/*"
                    element={
                      <PrivateRoute>
                        <AdminRoute />
                      </PrivateRoute>
                    }
                  />
                  {/* <Route path="/users" element={<PrivateRoute><UsersPage /></PrivateRoute>} /> */}
                  <Route
                    path="/dashboard"
                    element={<Navigate to="/" replace />}
                  />
                  <Route
                    path="/onboarding"
                    element={
                      <AuthRequiredRoute>
                        <OnboardingPage />
                      </AuthRequiredRoute>
                    }
                  />
                </Routes>
              </Layout>
            </AuthRedirect>
          }
        />
      </Routes>
    </Router>
  );
}

/**
 * GA4 の page_view をルート遷移ごとに送るためだけの空コンポーネント。
 * useLocation を使うため Router の内側に置く必要がある。
 * @return {null} 描画物なし
 */
const AnalyticsPageViewTracker: FC = () => {
  useAnalyticsPageView();
  return null;
};

/**
 * 旧 /companies/:id から /settings/companies/:id へのリダイレクト。
 * @return {JSX.Element} Navigate 要素
 */
const RedirectCompanyDetail: FC = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/settings/companies/${id ?? ""}`} replace />;
};

/**
 * 旧 /company-groups/:id から /settings/company-groups/:id へのリダイレクト。
 * @return {JSX.Element} Navigate 要素
 */
const RedirectCompanyGroupDetail: FC = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/settings/company-groups/${id ?? ""}`} replace />;
};

/**
 * ルートパス（/）の振り分け
 * 認証済み → ダッシュボードを / で表示、未認証 → LP（feature flag 有効時）またはログイン画面
 * @return {JSX.Element} 認証状態に応じたコンポーネント
 */
const RootRoute: FC = () => {
  const { currentUser, isLoadingUser } = useContext(AuthContext);
  const { flags, loading: flagsLoading } = usePublicFeatureFlags();
  const lpEnabled = flags["ui.landing_page"] === true;

  // 実 URL は / のままで、GA4 上のパスだけを分ける。
  // 解決するまでは送らない（どちらを表示するか決まっていないため）。
  // /login へ Navigate する場合も送らない（遷移先の page_view で拾われる）。
  const resolved = !isLoadingUser && !flagsLoading;
  const isAuthenticated = Boolean(currentUser);
  let logicalPath: string | null = null;
  if (resolved) {
    if (isAuthenticated) {
      logicalPath = "/app";
    } else if (lpEnabled) {
      logicalPath = "/lp";
    }
  }
  useLogicalPageView(logicalPath);

  if (isLoadingUser || flagsLoading) {
    return <FullScreenLoading />;
  }

  if (currentUser) {
    return (
      <AuthRedirect>
        <Layout>
          <PrivateRoute>
            <HomePage />
          </PrivateRoute>
        </Layout>
      </AuthRedirect>
    );
  }

  if (lpEnabled) {
    return <LandingPage />;
  }

  return <Navigate to="/login" replace />;
};

/**
 * /lp ルート: feature flag で LP の表示を制御
 * @return {JSX.Element} LP または 404
 */
const LpRoute: FC = () => {
  const { flags, loading } = usePublicFeatureFlags();
  const lpEnabled = flags["ui.landing_page"] === true;

  // / と /lp はどちらも同じ LP なので、論理パスは /lp に揃える。
  // 実 URL を跨いだ移動では追加の page_view を送らない（同じ 1 ページ）。
  useLogicalPageView(!loading && lpEnabled ? "/lp" : null);

  if (loading) {
    return <FullScreenLoading />;
  }

  if (lpEnabled) {
    return <LandingPage />;
  }

  return <Navigate to="/login" replace />;
};

/**
 * 認証のみ必須のルート（テナントチェックなし）
 * オンボーディングページ用
 * @return {JSX.Element} 認証済みの場合は子要素、未認証の場合はリダイレクト
 */
const AuthRequiredRoute: FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, isLoadingUser, authError } = useContext(AuthContext);

  if (isLoadingUser) {
    return <FullScreenLoading />;
  }

  if (authError) {
    return <Navigate to="/login" replace />;
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

/**
 * 認証＋テナント必須のルート
 * Python Backend 使用時: tenants が空なら /onboarding へリダイレクト
 * TS Backend 使用時: tenants が undefined なのでスキップ
 * @return {JSX.Element} 認証・テナント済みの場合は子要素、それ以外はリダイレクト
 */
const PrivateRoute: FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, isLoadingUser, authError } = useContext(AuthContext);

  if (isLoadingUser) {
    return <FullScreenLoading />;
  }

  if (authError) {
    return <Navigate to="/login" replace />;
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  // Python Backend: tenants が存在するが空の場合はオンボーディングへ
  // TS Backend: tenants が undefined なのでこのチェックはスキップ
  if (currentUser.tenants !== undefined && currentUser.tenants.length === 0) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
};

const AuthRedirect: FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();

  const redirectToPreviousPageAfterLogin = useCallback(
    (data: HubCapsule<"auth", any>) => {
      const { payload } = data;
      switch (payload.event) {
        case "customOAuthState":
          navigate(decodeURIComponent(payload.data));
          break;
        case "signIn_failure":
          navigate("/");
          break;
      }
    },
    [navigate],
  );

  useEffect(() => {
    const listener = Hub.listen("auth", redirectToPreviousPageAfterLogin);
    return () => {
      listener();
    };
  }, [redirectToPreviousPageAfterLogin]);

  return <>{children}</>;
};

export default App;
